import { chain } from 'lodash'
import { BasicEmotion, Emotion } from 'db-tables'

import { isAngleInArc, arcSize } from './angleOpetations'

// Finds emotion words which arcs defined by their angles contain a specific angle.
// Sorts the matching emotion words by "narrowness" of their arc.
// Allows for passing a specified word to be included at first position.
// This is useful for showing a selected word at first position.
//
// Details:
// Let's present the 0 to 360 angles band as a line:
//
// 0°                180°               360°
// |------------------|------------------|
//
// where 0 = 360.
//
// Words define an arc by their start and end angles.
// For example the word Calm which start from 90° and end at 270°:
//
// 0°                180°               360°
// |------------------|------------------|
//          ^       Calm       ^
//          |------------------|
//         90°                270°
//
//
// Let's s see a case with 2 words' arcs that contain
// the specific angle:
//
// Calm - from 90° to 270°
// Tranquil - from 170° to 230°
// The specified angle is 210°
//
//                      210° = specified angle
//                       |
// 0°                180°|              360°
// |------------------|--|---------------|
//          ^        ^     ^   ^
//          |        |-----|   |
//          |       170°  230° |
//          |------------------|
//         90°                270°
//
// Both Calm and Tranquil arcs contain the specified
// angle.
// They need to be sorted by their arcs narrowness.
// Tranquil's arc is narrower at 230° - 170° = 60°,
// compared to Calm's 270° - 90° = 180°.
// => the will be sorted:
// 1. Tranquil
// 2. Calm
//
// This algorithm allows presenting emotions with a higher
// "intensity" by having a narrower arc. These emotions
// will appear higher at the sorting of words.
// => When rotating the dial "high intensity" words appear
// first for shorter time(narrower arc) while "lower
// intensity" words(wider arc) appear later in the list
// but stay visible for more time.

export const MAX_NUMBER_OF_WORDS = 12

export default function emotionWordsByAngle({
  angle,
  emotionWords,
  wordToInclude,
  selectedEmotionWords,
  limit = MAX_NUMBER_OF_WORDS,
}: {
  angle: number
  emotionWords: Emotion[] | BasicEmotion[]
  wordToInclude?: Emotion | BasicEmotion
  selectedEmotionWords: Emotion[] | BasicEmotion[]
  limit?: number
}) {
  const sortedWords = chain(emotionWords)
    .filter((word) => {
      const inArc = isAngleInArc(angle, word.angle_start, word.angle_end)
      // If the passed word to include is specified, make sure to return it.
      // Whether ot not the specified angle lies in it's arc.
      if (word.id === wordToInclude?.id && inArc) {
        return true
      }
      return inArc
    })
    .sortBy((word) => {
      // Sort by arc size. Ascending - from smaller(narrower) arcs to bigger(wider).
      return arcSize(word.angle_start, word.angle_end)
    })

  return (
    sortedWords
      .orderBy((word) => {
        const wordIndex = sortedWords.value().findIndex((w) => w.id === word.id)
        const isSelected = selectedEmotionWords.some((w) => w.id === word.id)

        // Show the word in the end of the limited list when selected
        if (wordIndex >= limit && isSelected) {
          return limit - selectedEmotionWords.length - 1
        }

        // Show the word at it's original arc position
        return wordIndex
      })
      // .take(limit)
      .value()
  )
}
